import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { useAuth0 } from '../../contexts/auth0-context'
import endpoints from '../../API/endpoints'
import callAPI from '../../API/callSecureAPI'
import { setIsPicoEnabled, setIsMaiaEnabled } from '../../redux/actions'
import { OrganizationIntegrationsMap } from '../constants/integrations'

export type Organization = {
    _id: string
    name: string
    // The product name shown to this org's users (almost always 'ATLAS'; a legacy exception like
    // Sanofi's 'SPOT' keeps its pre-rebrand name). Distinct from `name`, the org's own business name.
    platformName?: string
    confidentialityFooter: string
    image?: string
    lightModeImage?: string
    isFreeTrial?: boolean
    freeTrialStartDate?: string
    isPicoEnabled?: boolean
    isMaiaEnabled?: boolean
    isAIEnabled?: boolean
    // Undefined/true means normal per-roadmap StrategyPermissions gating; false means every user
    // in the org reaches every roadmap regardless of tier -- see resolveOrgWideTier server-side.
    isAllowedUsersEnabled?: boolean
    integrations?: OrganizationIntegrationsMap
}

const ONE_MINUTE = 1000 * 60
const FIVE_MINUTES = ONE_MINUTE * 5

export const useGetOrganizationQuery = () => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    const dispatch = useDispatch()

    const onGetOrganization = (organization: Organization) => {
        dispatch(setIsPicoEnabled(Boolean(organization?.isPicoEnabled)))
        dispatch(setIsMaiaEnabled(Boolean(organization?.isMaiaEnabled)))
    }

    // @ts-expect-error store is not typed properly
    const organizationId = useSelector((state) => state.userRecord?.organizationId)

    const getOrganization = async () => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

            const { url, method } = endpoints.getOrganizationById

            const response = await callAPI(url + organizationId, method, token)

            return response.data
        } catch (e) {
            console.log(e)
        }
    }

    return useQuery<Organization>(['organization', organizationId], getOrganization, {
        enabled: !!getTokenSilently && !!isAuthenticated && !isLoading,
        cacheTime: FIVE_MINUTES,
        staleTime: FIVE_MINUTES,
        retry: 3,
        onSuccess: onGetOrganization,
    })
}
